import React from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, services } from '../../data/mock';
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Instagram,
  Facebook,
  Twitter,
  ArrowRight,
  Clock,
} from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Logo URL
  const logoUrl = "https://customer-assets.emergentagent.com/job_aa40e8f2-db60-4704-ab8e-d5a0df164063/artifacts/mbipe7fe_Frame%203.svg";

  return (
    <footer className="relative bg-[#080c10] border-t border-[#1e293b]">
      {/* Gradient Accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#329780]/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center">
              <img 
                src={logoUrl} 
                alt="KC Byte iT" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Soluções completas em tecnologia da informação para empresas que
              buscam eficiência, segurança e crescimento sustentável.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href={siteConfig.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#111920] flex items-center justify-center text-slate-400 hover:text-[#329780] hover:bg-[#329780]/10 transition-all duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={siteConfig.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#111920] flex items-center justify-center text-slate-400 hover:text-[#329780] hover:bg-[#329780]/10 transition-all duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={siteConfig.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#111920] flex items-center justify-center text-slate-400 hover:text-[#329780] hover:bg-[#329780]/10 transition-all duration-200"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={siteConfig.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#111920] flex items-center justify-center text-slate-400 hover:text-[#329780] hover:bg-[#329780]/10 transition-all duration-200"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-white font-semibold text-lg">Links Rápidos</h4>
            <nav className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Quem Somos', path: '/quem-somos' },
                { name: 'Serviços', path: '/servicos' },
                { name: 'Blog', path: '/blog' },
                { name: 'Contato', path: '/contato' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center text-slate-400 hover:text-[#329780] transition-colors duration-200 group"
                >
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  <span>{link.name}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h4 className="text-white font-semibold text-lg">Serviços</h4>
            <nav className="space-y-3">
              {services.slice(0, 5).map((service) => (
                <Link
                  key={service.id}
                  to="/servicos"
                  className="flex items-center text-slate-400 hover:text-[#329780] transition-colors duration-200 group text-sm"
                >
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 flex-shrink-0" />
                  <span className="line-clamp-1">{service.title}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-6">
            <h4 className="text-white font-semibold text-lg">Contato</h4>
            <div className="space-y-4">
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-start text-slate-400 hover:text-[#329780] transition-colors duration-200 group"
              >
                <Mail className="w-5 h-5 mr-3 mt-0.5 text-[#329780]" />
                <span>{siteConfig.email}</span>
              </a>
              <a
                href={`tel:${siteConfig.phone.replace(/\D/g, '')}`}
                className="flex items-start text-slate-400 hover:text-[#329780] transition-colors duration-200 group"
              >
                <Phone className="w-5 h-5 mr-3 mt-0.5 text-[#329780]" />
                <span>{siteConfig.phone}</span>
              </a>
              <div className="flex items-start text-slate-400">
                <MapPin className="w-5 h-5 mr-3 mt-0.5 text-[#329780]" />
                <span>{siteConfig.address}</span>
              </div>
              <div className="flex items-start text-slate-400">
                <Clock className="w-5 h-5 mr-3 mt-0.5 text-[#329780]" />
                <span>{siteConfig.workingHours}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#1e293b]">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-slate-500 text-sm">
              © {currentYear} KC Byte iT. Todos os direitos reservados.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link
                to="/"
                className="text-slate-500 hover:text-[#329780] transition-colors duration-200"
              >
                Política de Privacidade
              </Link>
              <Link
                to="/"
                className="text-slate-500 hover:text-[#329780] transition-colors duration-200"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
